import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  FormGroup,
  TextField,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import YouTubeIcon from "@mui/icons-material/YouTube";
import BusinessIcon from "@mui/icons-material/Business";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";
import api from "../../../conf/api";
import { uploadFileInChunks } from "../utils/chunkUploader";
import { Scrollbars } from "react-custom-scrollbars-2";
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
/**
 * Step 3: Multi-Level Destination Selection
 * Independent selection for Brand and multiple Subbrands (MRSS + YouTube)
 */
function DestinationSelection({
  selectedVideos = [],
  videoSources = {},
  rephrasedContent = {},
  destinations = {},
  handleDestinationChange,
  onReset,
  videoPlatforms = {},
  videoConvertToVertical = {},
}) {
  const [brands, setBrands] = useState([]);
  const [displayBrands, setDisplayBrands] = useState(brands);
  const [feeds, setFeeds] = useState([]);
  const [subbrands, setSubbrands] = useState([]);
  const [youtubeChannels, setYoutubeChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Multi-brand selections structure: { brandName: { feeds: [], youtubeChannels: [], subbrands: {} } }
  // subbrands structure: { subbrandId: { feeds: [], youtubeChannels: [] } }
  const [multiBrandSelections, setMultiBrandSelections] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [brandsRes, feedsRes, youtubeRes] = await Promise.all([
        api.get("/mrss/brands/", { params: { page_size: 1000 } }),
        api.get("/mrss/feeds/"),
        api.get("/youtube/channels"),
      ]);

      setBrands(brandsRes.data.results || brandsRes.data);
      setDisplayBrands(brandsRes.data.results || brandsRes.data);
      setFeeds(feedsRes.data.results || feedsRes.data);
      setYoutubeChannels(youtubeRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load brands and feeds");
    } finally {
      setLoading(false);
    }
  };

  // Toggle brand selection (add/remove)
  const handleBrandToggle = async (brand) => {
    setMultiBrandSelections((prev) => {
      if (prev[brand.name]) {
        // Remove brand
        const { [brand.name]: removed, ...rest } = prev;
        return rest;
      } else {
        // Add brand with empty selections
        return {
          ...prev,
          [brand.name]: {
            feeds: [],
            youtubeChannels: [],
            subbrands: {},
          },
        };
      }
    });

    // Fetch subbrands for this brand if not already loaded
    if (!multiBrandSelections[brand.name]) {
      try {
        const response = await api.get("/mrss/subbrands/", {
          params: { brand: brand.name },
        });
        const fetchedSubbrands = response.data.results || response.data;

        // Store subbrands in state for this brand
        setSubbrands((prev) => ({
          ...prev,
          [brand.name]: fetchedSubbrands,
        }));
      } catch (error) {
        console.error("Error fetching subbrands:", error);
      }
    }
  };

  // Brand-level feed toggle
  const handleBrandFeedToggle = (brandName, feedId) => {
    setMultiBrandSelections((prev) => {
      const brandData = prev[brandName];
      return {
        ...prev,
        [brandName]: {
          ...brandData,
          feeds: brandData.feeds.includes(feedId)
            ? brandData.feeds.filter((id) => id !== feedId)
            : [...brandData.feeds, feedId],
        },
      };
    });
  };

  // Brand-level YouTube toggle
  const handleBrandYoutubeToggle = (brandName, channelName) => {
    setMultiBrandSelections((prev) => {
      const brandData = prev[brandName];
      return {
        ...prev,
        [brandName]: {
          ...brandData,
          youtubeChannels: brandData.youtubeChannels.includes(channelName)
            ? brandData.youtubeChannels.filter((name) => name !== channelName)
            : [...brandData.youtubeChannels, channelName],
        },
      };
    });
  };

  // Subbrand-level feed toggle
  const handleSubbrandFeedToggle = (brandName, subbrandId, feedId) => {
    setMultiBrandSelections((prev) => {
      const brandData = prev[brandName];
      const subbrandData = brandData.subbrands[subbrandId] || {
        feeds: [],
        youtubeChannels: [],
      };

      return {
        ...prev,
        [brandName]: {
          ...brandData,
          subbrands: {
            ...brandData.subbrands,
            [subbrandId]: {
              ...subbrandData,
              feeds: subbrandData.feeds.includes(feedId)
                ? subbrandData.feeds.filter((id) => id !== feedId)
                : [...subbrandData.feeds, feedId],
            },
          },
        },
      };
    });
  };

  // Subbrand-level YouTube toggle
  const handleSubbrandYoutubeToggle = (brandName, subbrandId, channelName) => {
    setMultiBrandSelections((prev) => {
      const brandData = prev[brandName];
      const subbrandData = brandData.subbrands[subbrandId] || {
        feeds: [],
        youtubeChannels: [],
      };

      return {
        ...prev,
        [brandName]: {
          ...brandData,
          subbrands: {
            ...brandData.subbrands,
            [subbrandId]: {
              ...subbrandData,
              youtubeChannels: subbrandData.youtubeChannels.includes(channelName)
                ? subbrandData.youtubeChannels.filter((name) => name !== channelName)
                : [...subbrandData.youtubeChannels, channelName],
            },
          },
        },
      };
    });
  };

  const handlePublish = async () => {
    // Validation - calculate totals across all selected brands
    let totalFeedsSelected = 0;
    let totalYoutubeSelected = 0;

    Object.values(multiBrandSelections).forEach((brandData) => {
      totalFeedsSelected += brandData.feeds.length;
      totalYoutubeSelected += brandData.youtubeChannels.length;

      // Add subbrand selections
      Object.values(brandData.subbrands || {}).forEach((subbrandData) => {
        totalFeedsSelected += subbrandData.feeds.length;
        totalYoutubeSelected += subbrandData.youtubeChannels.length;
      });
    });

    if (totalFeedsSelected === 0 && totalYoutubeSelected === 0) {
      toast.warning(
        "Please select at least one feed or YouTube channel from any brand"
      );
      return;
    }

    try {
      setUploading(true);

      // Helper function to convert base64 to File
      const base64ToFile = (base64String, filename) => {
        const arr = base64String.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
      };

      // Upload large files first using chunked upload
      const videosDataWithPaths = [];

      for (let index = 0; index < selectedVideos.length; index++) {
        const video = selectedVideos[index];
        const videoSource = videoSources[video.unique_key];
        const thumbnail = video.thumbnail; // Get thumbnail from video object
        const rephrased = rephrasedContent[video.unique_key]; // Get rephrased content if available

        const videoData = {
          unique_key: video.unique_key,
          video_source: videoSource.source,
          original_video_url: video.s3_url || "",
          title: rephrased?.title || video.title || video.video_title,
          description: rephrased?.description || video.description || "",
          keywords: rephrased?.keywords || video.keywords,
          platforms: videoPlatforms[video.unique_key] || [],
          convert_to_vertical: videoConvertToVertical[video.unique_key] || false,
          ...(videoSource.source === "EDITED" && videoSource.renderPayload && {
            render_payload: videoSource.renderPayload,
          }),
        };
        // Check if thumbnail is a URL (starts with http or https)
        if (typeof thumbnail === "string" && thumbnail.startsWith("http")) {
          videoData.original_thumbnail_url = thumbnail;
        }


        // Handle local video file - use chunked upload for large files (>10MB)
        if (videoSource.source === "LOCAL" && videoSource.file) {
          const videoFile = videoSource.file;
          const fileSizeMB = videoFile.size / (1024 * 1024);

          if (fileSizeMB > 10) {
            toast.info(`Uploading ${video.unique_key} (${fileSizeMB.toFixed(1)} MB)...`);

            try {
              const uploadResult = await uploadFileInChunks(videoFile, 'video', {
                onProgress: (progress) => {
                  setUploadProgress((prev) => ({
                    ...prev,
                    [video.unique_key]: {
                      type: 'video',
                      progress: progress.progress,
                      filename: videoFile.name,
                    },
                  }));
                },
              });

              videoData.local_video_path = uploadResult.file_path;
              toast.success(`Video ${video.unique_key} uploaded`);
            } catch (error) {
              throw new Error(`Failed to upload video ${video.unique_key}: ${error.message}`);
            }
          } else {
            videoData.local_video_file_small = videoFile;
          }
        }

        // Handle thumbnail - use chunked upload for large thumbnails (>5MB)
        if (thumbnail) {
          let thumbnailFile;

          if (typeof thumbnail === 'string' && thumbnail.startsWith('data:image')) {
            thumbnailFile = base64ToFile(thumbnail, `thumbnail_${video.unique_key}.png`);
          } else if (thumbnail instanceof File) {
            thumbnailFile = thumbnail;
          }

          if (thumbnailFile) {
            const thumbSizeMB = thumbnailFile.size / (1024 * 1024);

            if (thumbSizeMB > 5) {
              try {
                const uploadResult = await uploadFileInChunks(thumbnailFile, 'thumbnail', {
                  onProgress: (progress) => {
                    setUploadProgress((prev) => ({
                      ...prev,
                      [`${video.unique_key}_thumb`]: {
                        type: 'thumbnail',
                        progress: progress.progress,
                      },
                    }));
                  },
                });

                videoData.thumbnail_path = uploadResult.file_path;
              } catch (error) {
                console.error(`Failed to upload thumbnail for ${video.unique_key}:`, error);
              }
            } else {
              videoData.thumbnail_file_small = thumbnailFile;
            }
          }
        }

        // Add brand selections
        videoData.brand_selections = Object.entries(multiBrandSelections).map(
          ([brandName, brandData]) => ({
            brand_name: brandName,
            feeds: brandData.feeds,
            youtube_channels: brandData.youtubeChannels,
            subbrand_selections: Object.entries(brandData.subbrands || {}).map(
              ([subbrandId, subbrandData]) => ({
                subbrand_id: parseInt(subbrandId),
                feeds: subbrandData.feeds,
                youtube_channels: subbrandData.youtubeChannels,
              })
            ),
          })
        );

        videosDataWithPaths.push(videoData);
      }

      // Check if we have small files to upload
      const hasSmallFiles = videosDataWithPaths.some(
        (vd) => vd.local_video_file_small || vd.thumbnail_file_small
      );

      let response;

      if (hasSmallFiles) {
        // Use FormData for remaining small files
        const formData = new FormData();

        videosDataWithPaths.forEach((videoData, index) => {
          Object.entries(videoData).forEach(([key, value]) => {
            if (key === 'local_video_file_small') {
              formData.append(`videos[${index}][local_video_file]`, value);
            } else if (key === 'thumbnail_file_small') {
              formData.append(`videos[${index}][thumbnail_file]`, value);
            } else if (key === 'brand_selections' || key === 'platforms' || key === 'render_payload') {
              formData.append(`videos[${index}][${key}]`, JSON.stringify(value));
            } else if (value !== undefined && value !== null) {
              formData.append(`videos[${index}][${key}]`, value);
            }
          });
        });

        response = await api.post("/mrss/upload/", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 300000, // 5 minutes
        });
      } else {
        // No small files - all files uploaded via chunked upload
        // Use the videosDataWithPaths which contains the file paths
        const uploadData = {
          videos: videosDataWithPaths.map((videoData) => {
            // Remove the _small file properties as they don't exist
            const { local_video_file_small, thumbnail_file_small, ...cleanVideoData } = videoData;
            return cleanVideoData;
          }),
        };

        response = await api.post("/mrss/upload/", uploadData, { timeout: 60 * 60 * 5 });
      }

      // Upload to MRSS feeds
      if (totalFeedsSelected > 0) {
        toast.success(
          `Successfully queued ${selectedVideos.length} video(s) for ${totalFeedsSelected} feed(s) across ${Object.keys(multiBrandSelections).length} brand(s)`
        );
      }

      // Upload to YouTube (if any selected)
      if (totalYoutubeSelected > 0) {
        // YouTube upload logic would go here
        toast.info(
          `YouTube publishing for ${totalYoutubeSelected} channel(s) will be processed`
        );
      }

      // Reset the wizard
      if (onReset) {
        setTimeout(() => {
          onReset();
        }, 2000);
      }
    } catch (error) {
      console.error("Error uploading:", error);
      toast.error(error.response?.data?.error || "Failed to upload videos");
    } finally {
      setUploading(false);
    }
  };
  const handleBrandSearch = (e) => {
    const search = e.target?.value?.trim()?.toLowerCase();
    if (!search || search === "" || search.length < 1) {
      setDisplayBrands(brands);
      return;
    }
    const _brands = brands.filter(b => b.name?.toLowerCase().includes(search));
    setDisplayBrands(_brands);
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  // Helper function to get YouTube channels for a specific brand
  const getYoutubeChannelsForBrand = (brandName) => {
    return youtubeChannels.filter((channel) =>
      channel.brand_name === brandName && !channel.subbrand_id
    );
  };

  // Helper function to get YouTube channels for a specific subbrand
  const getYoutubeChannelsForSubbrand = (subbrandId) => {
    return youtubeChannels.filter((channel) =>
      channel.subbrand_id === subbrandId
    );
  };

  // Calculate summary statistics
  let totalFeedsInSummary = 0;
  let totalYoutubeInSummary = 0;
  let totalSubbrandsInSummary = 0;

  Object.values(multiBrandSelections).forEach((brandData) => {
    totalFeedsInSummary += brandData.feeds.length;
    totalYoutubeInSummary += brandData.youtubeChannels.length;

    const subbrandCount = Object.keys(brandData.subbrands || {}).length;
    totalSubbrandsInSummary += subbrandCount;

    Object.values(brandData.subbrands || {}).forEach((subbrandData) => {
      totalFeedsInSummary += subbrandData.feeds.length;
      totalYoutubeInSummary += subbrandData.youtubeChannels.length;
    });
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Multi-Brand Publishing Selection
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        Select multiple brands and configure destinations for {selectedVideos.length} video(s)
      </Typography>

      <Grid container spacing={3}>
        {/* Left: Brand Selection */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <BusinessIcon color="primary" />
              <Typography variant="subtitle1">Select Brands</Typography>
              <Chip
                label={`${Object.keys(multiBrandSelections).length} selected`}
                size="small"
                color={Object.keys(multiBrandSelections).length > 0 ? "primary" : "default"}
              />
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
              <TextField
                size="small"
                margin="dense"
                placeholder="Search Brands"
                InputProps={{ endAdornment: <InputAdornment position="end"><SearchOutlinedIcon /></InputAdornment> }}
                onChange={handleBrandSearch}
              />
              <Scrollbars style={{ height: "60vh", width: "100%" }}>
                {displayBrands.map((brand) => (
                  <Paper
                    key={brand.name}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      border: "2px solid",
                      borderColor: multiBrandSelections[brand.name]
                        ? "primary.main"
                        : "transparent",
                      bgcolor: multiBrandSelections[brand.name]
                        ? "primary.50"
                        : "grey.50",
                    }}
                    onClick={() => handleBrandToggle(brand)}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="subtitle2">{brand.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {brand.feeds_count} feeds • {brand.subbrands_count} subbrands
                        </Typography>
                      </Box>
                      <Checkbox
                        checked={!!multiBrandSelections[brand.name]}
                        size="small"
                        readOnly
                      />
                    </Box>
                  </Paper>
                ))}
              </Scrollbars>
            </Box>
          </Paper>
        </Grid>

        {/* Right: Multi-Brand Selections */}
        <Grid item xs={12} md={8}>
          {Object.keys(multiBrandSelections).length > 0 ? (
            <Box display="flex" flexDirection="column" gap={2}>
              {/* Selected Brands Accordions */}
              {Object.entries(multiBrandSelections).map(([brandName, brandData]) => {
                const brand = brands.find((b) => b.name === brandName);
                if (!brand) return null;

                const brandFeeds = feeds.filter(
                  (f) => f.brand_name === brand.name && !f.subbrand
                );
                const brandSubbrands = subbrands[brandName] || [];

                return (
                  <Accordion
                    key={brandName}
                    defaultExpanded
                    sx={{ border: "2px solid", borderColor: "primary.main" }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1} width="100%">
                        <BusinessIcon color="primary" />
                        <Typography variant="h6" sx={{ flex: 1 }}>
                          {brand.name}
                        </Typography>
                        <Chip
                          label={`${brandData.feeds.length} feeds`}
                          size="small"
                          color={brandData.feeds.length > 0 ? "success" : "default"}
                        />
                        <Chip
                          label={`${brandData.youtubeChannels.length} YT`}
                          size="small"
                          color={brandData.youtubeChannels.length > 0 ? "error" : "default"}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box display="flex" flexDirection="column" gap={2}>
                        {/* Brand MRSS Feeds */}
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <RssFeedIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle2">MRSS Feeds</Typography>
                            <Chip
                              label={`${brandData.feeds.length} selected`}
                              size="small"
                              color={brandData.feeds.length > 0 ? "success" : "default"}
                            />
                          </Box>

                          {brandFeeds.length > 0 ? (
                            <FormGroup>
                              {brandFeeds.map((feed) => (
                                <FormControlLabel
                                  key={feed.id}
                                  control={
                                    <Checkbox
                                      checked={brandData.feeds.includes(feed.id)}
                                      onChange={() => handleBrandFeedToggle(brandName, feed.id)}
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Typography variant="body2">{feed.name}</Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        {feed.feed_type} • {feed.items_count} items
                                      </Typography>
                                    </Box>
                                  }
                                />
                              ))}
                            </FormGroup>
                          ) : (
                            <Alert severity="info" sx={{ mt: 1 }}>
                              No feeds available
                            </Alert>
                          )}
                        </Box>

                        <Divider />

                        {/* Brand YouTube Channels */}
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <YouTubeIcon fontSize="small" color="error" />
                            <Typography variant="subtitle2">YouTube Channels</Typography>
                            <Chip
                              label={`${brandData.youtubeChannels.length} selected`}
                              size="small"
                              color={brandData.youtubeChannels.length > 0 ? "error" : "default"}
                            />
                          </Box>

                          {(() => {
                            const brandYoutubeChannels = getYoutubeChannelsForBrand(brandName);
                            return brandYoutubeChannels.length > 0 ? (
                              <FormGroup>
                                {brandYoutubeChannels.map((channel) => (
                                  <FormControlLabel
                                    key={channel.channel_name}
                                    control={
                                      <Checkbox
                                        checked={brandData.youtubeChannels.includes(
                                          channel.channel_name
                                        )}
                                        onChange={() =>
                                          handleBrandYoutubeToggle(brandName, channel.channel_name)
                                        }
                                        size="small"
                                      />
                                    }
                                    label={
                                      <Typography variant="body2">
                                        {channel.channel_name}
                                      </Typography>
                                    }
                                  />
                                ))}
                              </FormGroup>
                            ) : (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                No YouTube channels associated with this brand
                              </Alert>
                            );
                          })()}
                        </Box>

                        {/* Subbrands for this brand */}
                        {brandSubbrands.length > 0 && (
                          <>
                            <Divider />
                            <Box>
                              <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <AccountTreeIcon color="secondary" />
                                <Typography variant="subtitle1">
                                  Subbrands ({brandSubbrands.length})
                                </Typography>
                              </Box>

                              {brandSubbrands.map((subbrand) => {
                                const subbrandFeeds = feeds.filter(
                                  (f) => f.subbrand === subbrand.id
                                );
                                const subbrandData = brandData.subbrands[subbrand.id] || {
                                  feeds: [],
                                  youtubeChannels: [],
                                };

                                return (
                                  <Accordion
                                    key={subbrand.id}
                                    sx={{ mb: 1, border: "1px solid", borderColor: "grey.300" }}
                                  >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        width="100%"
                                      >
                                        <AccountTreeIcon fontSize="small" color="secondary" />
                                        <Typography variant="subtitle2" sx={{ flex: 1 }}>
                                          {subbrand.name}
                                        </Typography>
                                        <Chip
                                          label={`${subbrandData.feeds.length} feeds`}
                                          size="small"
                                          color={
                                            subbrandData.feeds.length > 0 ? "success" : "default"
                                          }
                                        />
                                        <Chip
                                          label={`${subbrandData.youtubeChannels.length} YT`}
                                          size="small"
                                          color={
                                            subbrandData.youtubeChannels.length > 0
                                              ? "error"
                                              : "default"
                                          }
                                        />
                                      </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      {/* Subbrand MRSS Feeds */}
                                      <Box mb={2}>
                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                          <RssFeedIcon fontSize="small" color="success" />
                                          <Typography variant="subtitle2">MRSS Feeds</Typography>
                                        </Box>

                                        {subbrandFeeds.length > 0 ? (
                                          <FormGroup>
                                            {subbrandFeeds.map((feed) => (
                                              <FormControlLabel
                                                key={feed.id}
                                                control={
                                                  <Checkbox
                                                    checked={subbrandData.feeds.includes(feed.id)}
                                                    onChange={() =>
                                                      handleSubbrandFeedToggle(
                                                        brandName,
                                                        subbrand.id,
                                                        feed.id
                                                      )
                                                    }
                                                    size="small"
                                                  />
                                                }
                                                label={
                                                  <Box>
                                                    <Typography variant="body2">
                                                      {feed.name}
                                                    </Typography>
                                                    <Typography
                                                      variant="caption"
                                                      color="textSecondary"
                                                    >
                                                      {feed.feed_type} • {feed.items_count} items
                                                    </Typography>
                                                  </Box>
                                                }
                                              />
                                            ))}
                                          </FormGroup>
                                        ) : (
                                          <Alert severity="info">No feeds available</Alert>
                                        )}
                                      </Box>

                                      {/* Subbrand YouTube Channels */}
                                      <Box>
                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                          <YouTubeIcon fontSize="small" color="error" />
                                          <Typography variant="subtitle2">
                                            YouTube Channels
                                          </Typography>
                                        </Box>

                                        {(() => {
                                          const subbrandYoutubeChannels = getYoutubeChannelsForSubbrand(subbrand.id);
                                          return subbrandYoutubeChannels.length > 0 ? (
                                            <FormGroup>
                                              {subbrandYoutubeChannels.map((channel) => (
                                                <FormControlLabel
                                                  key={channel.channel_name}
                                                  control={
                                                    <Checkbox
                                                      checked={subbrandData.youtubeChannels.includes(
                                                        channel.channel_name
                                                      )}
                                                      onChange={() =>
                                                        handleSubbrandYoutubeToggle(
                                                          brandName,
                                                          subbrand.id,
                                                          channel.channel_name
                                                        )
                                                      }
                                                      size="small"
                                                    />
                                                  }
                                                  label={
                                                    <Typography variant="body2">
                                                      {channel.channel_name}
                                                    </Typography>
                                                  }
                                                />
                                              ))}
                                            </FormGroup>
                                          ) : (
                                            <Alert severity="info">
                                              No YouTube channels associated with this subbrand
                                            </Alert>
                                          );
                                        })()}
                                      </Box>
                                    </AccordionDetails>
                                  </Accordion>
                                );
                              })}
                            </Box>
                          </>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}

              {/* Publishing Summary */}
              <Paper sx={{ p: 3, bgcolor: "primary.50" }}>
                <Typography variant="h6" gutterBottom>
                  Publishing Summary
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Videos:</strong> {selectedVideos.length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Selected Brands:</strong>{" "}
                      {Object.keys(multiBrandSelections).length}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Feeds:</strong> {totalFeedsInSummary}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Total Subbrands:</strong> {totalSubbrandsInSummary}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total YouTube:</strong> {totalYoutubeInSummary}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Upload Progress */}
                {uploading && Object.keys(uploadProgress).length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Upload Progress:
                    </Typography>
                    {Object.entries(uploadProgress).map(([key, progressData]) => (
                      <Box key={key} sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="caption">
                            {progressData.filename || key} ({progressData.type})
                          </Typography>
                          <Typography variant="caption" fontWeight="bold">
                            {progressData.progress.toFixed(0)}%
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: "100%",
                            height: 8,
                            bgcolor: "grey.300",
                            borderRadius: 1,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${progressData.progress}%`,
                              height: "100%",
                              bgcolor: "primary.main",
                              transition: "width 0.3s ease",
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  startIcon={<SendIcon />}
                  onClick={handlePublish}
                  disabled={uploading}
                  sx={{ mt: 3 }}
                >
                  {uploading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Videos Publishing"
                  )}
                </Button>
              </Paper>
            </Box>
          ) : (
            <Paper sx={{ p: 5, textAlign: "center" }}>
              <BusinessIcon sx={{ fontSize: 60, color: "grey.400", mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Select one or more brands to continue
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default DestinationSelection;
